import React, {PureComponent} from "react"
import styled from "styled-components"

import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"

import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {H2, Body, Label} from "@irati/ui/src/Typography"
import {IconTypes} from "@irati/ui/src/Icon"
import Button from "@irati/ui/src/Button"
import {Hr} from "@irati/ui/src/Layout"
import {RenewalOption} from "@irati/types"

import RenewalCard from "../../components/RenewalCard"
import RenewalHeader from "../../components/Renewal/Header"
import Detail from "../../components/Renewal/Detail"
import Progress from "../../components/Renewal/Progress"

interface Props extends InjectIntl {}

interface State {
  selectedQuote: RenewalOption
  confirmRenewalDetails: boolean
  accountContact: string
  email: string
  phone: string
  renewalDate: string
  showStatus: false
}

const LeftWidth = styled.div`
  max-width: 445px;
`

const quotes = [
  {
    termLength: 12,
    costPerSeatMonth: 8.3,
    isDefault: false,
    annualCost: 2000000,
    discount: undefined
  },
  {
    termLength: 24,
    costPerSeatMonth: 7.5,
    isDefault: true,
    annualCost: 1800000,
    discount: 11.8
  },
  {
    termLength: 36,
    costPerSeatMonth: 7.3,
    isDefault: false,
    annualCost: 1750000,
    discount: 14.2
  }
]

const progress = [
  {title: "admin.billing.renewals.progress.choosePlan.title", text: "admin.billing.renewals.progress.choosePlan.text", completed: true},
  {
    title: "admin.billing.renewals.progress.submitRenewal.title",
    text: "admin.billing.renewals.progress.submitRenewal.text",
    completed: true
  },
  {
    title: "admin.billing.renewals.progress.signContract.title",
    text: "admin.billing.renewals.progress.signContract.text",
    completed: false
  }
]

class RenewalQuoteSelection extends PureComponent<Props, State> {
  state: State = {
    selectedQuote: quotes[1],
    confirmRenewalDetails: false,
    showStatus: false,
    accountContact: "Lauren Ipsum",
    email: "lauren.ipsum@ipsymindustries.com",
    phone: "+44123456789",
    renewalDate: "24 Nov 2022"
  }

  componentDidMount() {
    this.setState({selectedQuote: quotes[1]})
  }

  handleUpdateSelected = (selectedQuote: RenewalOption) => {
    this.setState({selectedQuote})
  }

  handleConfirmRenewalDetails = () => {
    this.setState({confirmRenewalDetails: true})
  }

  handleBackToPlan = () => {
    this.setState({confirmRenewalDetails: false})
  }

  handleUpdateDetail = (key: string, value: string) => {
    this.setState({[key]: value})
  }

  getCurrentPlanChips = () => {
    const {formatMessage} = this.props
    const users = 300
    const points = 120
    return [
      {icon: IconTypes.searchPeople, label: formatMessage({id: "admin.billing.renewals.pills.users"}, {users})},
      {icon: IconTypes.flexi, label: formatMessage({id: "admin.billing.renewals.pills.flexiPoints"}, {points})},
      {icon: IconTypes.speech, label: formatMessage({id: "admin.billing.renewals.pills.eap"})}
    ]
  }

  render() {
    const {selectedQuote, confirmRenewalDetails, accountContact, email, phone, renewalDate, showStatus} = this.state
    const currentPlanChips = this.getCurrentPlanChips()

    if (showStatus) {
      return (
        <div>
          <Card bgColor={theme.colors.primary2} marginBottom={4} padding={5}>
            <Flex justifyContent="space-between">
              <div>
                <RenewalHeader
                  title="admin.billing.renewals.renewalSubmitted.title"
                  body="admin.billing.renewals.renewalSubmitted.body"
                  showPending
                  currentPlanChips={currentPlanChips}
                />
              </div>
              <RenewalCard hideSelector isSelected quote={selectedQuote} oneYearQuote={quotes[0]} />
            </Flex>
          </Card>
          <H2 marginBottom={1} textAlign="center">
            <FormattedMessage id="admin.billing.renewals.renewalSubmitted.progress.title" />
          </H2>
          <Body marginBottom={3} textAlign="center">
            <FormattedMessage id="admin.billing.renewals.renewalSubmitted.progress.body" />
          </Body>
          <Progress progress={progress} />
        </div>
      )
    }

    return !confirmRenewalDetails ? (
      <>
        <Card bgColor={theme.colors.primary2} marginBottom={2} padding={5}>
          <Flex justifyContent="space-between">
            <LeftWidth>
              <RenewalHeader
                title="admin.billing.renewals.newPlan.title"
                body="admin.billing.renewals.newPlan.body"
                currentPlanChips={currentPlanChips}
              />
            </LeftWidth>
            <div>
              <Flex gap={theme.spacing.one} marginBottom={1} justifyContent="flex-end">
                {quotes.map((quote, i) => (
                  <RenewalCard
                    quote={quote}
                    isSelected={quote.termLength === selectedQuote?.termLength}
                    onUpdate={this.handleUpdateSelected}
                    isRecommended={i === 1}
                    oneYearQuote={quotes[0]}
                  />
                ))}
              </Flex>
              <Label color={theme.colors.greys.mediumLight}>
                <FormattedMessage id="admin.billing.renewals.newPlan.lockIn" />
              </Label>
            </div>
          </Flex>
        </Card>
        <Button primary onClick={this.handleConfirmRenewalDetails}>
          <FormattedMessage id="admin.billing.renewals.newPlan.confirm" />
        </Button>
      </>
    ) : (
      <>
        <Card bgColor={theme.colors.primary2} marginBottom={4} padding={5}>
          <Flex>
            <div>
              <RenewalHeader
                title="admin.billing.renewals.newPlan.title"
                body="admin.billing.renewals.newPlan.body"
                currentPlanChips={currentPlanChips}
              />
            </div>
            <RenewalCard hideSelector isSelected quote={selectedQuote} oneYearQuote={quotes[0]} />
          </Flex>
        </Card>
        <H2 marginBottom={1}>
          <FormattedMessage id="admin.billing.renewals.planDetails.title" />
        </H2>
        <Body color={theme.colors.greys.medium} marginBottom={3}>
          <FormattedMessage id="admin.billing.renewals.planDetails.body" />
        </Body>
        <Detail
          currentValue={accountContact}
          title="admin.billing.renewals.planDetails.account.title"
          editText="admin.billing.renewals.planDetails.account.edit"
          onUpdate={(value) => this.setState({accountContact: value})}
        />
        <Detail
          currentValue={email}
          title="admin.billing.renewals.planDetails.email.title"
          editText="admin.billing.renewals.planDetails.email.edit"
          onUpdate={(value) => this.setState({email: value})}
        />
        <Detail
          currentValue={phone}
          title="admin.billing.renewals.planDetails.phone.title"
          editText="admin.billing.renewals.planDetails.phone.edit"
          onUpdate={(value) => this.setState({phone: value})}
        />
        <Detail isEditable={false} title="admin.billing.renewals.planDetails.renewal.title" currentValue={renewalDate} />
        <Hr />
        <Flex justifyContent="space-between">
          <Button primary onClick={this.handleConfirmRenewalDetails}>
            <FormattedMessage id="admin.billing.renewals.newPlan.submit" />
          </Button>
          <Button tertiary onClick={this.handleBackToPlan}>
            <FormattedMessage id="admin.billing.renewals.newPlan.back" />
          </Button>
        </Flex>
      </>
    )
  }
}

export default injectIntl(RenewalQuoteSelection)
